// The fields a report layout can contain, with the label, group and type the report (and its Excel
// export) uses. A layout stores the field keys; keys are the trial attribute names the old layouts
// used, so existing layouts keep working. Values come from POST /trials/{ids}, which loads the
// relations (customer, project.contract, crop, host, study_director, diaries, ...).
//
// type: 'text' | 'number' | 'date' | 'interval' (a date or free text, shown via formatDate) | 'bool' | 'enum' (with `options`: raw value -> label).
// value(trial) returns the raw value, or an array for diary fields (one report row per diary entry).
import { formatDate, formatDisplayDate } from '../../utils/dateFormat';
import { parseGps } from '../../utils/gps';
import { TRI_STATE_OPTIONS } from '../../utils/triState';
import { CO_LABEL } from '../../utils/contractLabel';
import { expectedText } from '../../utils/planning';
import { shownSamples } from '../../utils/samples';

export const REPORT_FIELD_GROUPS = [
  'Trial',
  'Customer, contract & project',
  'Crop & location',
  'People',
  'Dates & phases',
  // The activities, in the old system's three categories (names from the VA-code catalog)
  'Trial Activities',
  'Harvest Activities',
  'Report Activities',
  'Other status',
  'Tasks (diary entries)',
];

function getPath(obj, path) {
  return path.split('.').reduce((value, part) => {
    if (value == null) return undefined;
    if (Array.isArray(value)) return value.map((item) => item?.[part]);
    return value[part];
  }, obj);
}

const field = (key, label, group, type = 'text', extra = {}) => ({ key, label, group, type, ...extra });
const relation = (key, label, group, path) => field(key, label, group, 'text', { value: (t) => getPath(t, path) });
const check = (key, label, group) => field(key, label, group, 'bool');
// The samples of an activity, empty unless the activity is Yes: the number may be left over from before (#23)
const samples = (key, label) => field(key, label, 'Trial', 'number', { value: (t) => shownSamples(t, key) });
const TRI_STATE = Object.fromEntries(TRI_STATE_OPTIONS.map((o) => [o.value, o.label]));

const FIELDS = [
  // Trial
  field('trialno', 'Trial No', 'Trial'),
  field('Year', 'Year', 'Trial', 'number'),
  // Not offered for new layouts: stage is only kept up to date by Planning (parked), so nearly every
  // trial says "setup". Old layouts with it still show it.
  field('stage', 'Stage', 'Trial', 'text', { hidden: true }),
  field('status', 'Status', 'Trial'),
  field('Closed', 'Open/Closed', 'Trial', 'enum', { options: { true: 'Closed', false: 'Open', 1: 'Closed', 0: 'Open' } }),
  field('testitem', 'Test Item', 'Trial'),
  field('locationtarget', 'Location Target', 'Trial'),
  field('TrialAddress', 'Trial Address', 'Trial'),
  field('LocalReference', 'Local Reference', 'Trial'),
  field('subcontractor', 'Subcontractor', 'Trial'),
  field('TreatmentCount', 'Treatments', 'Trial', 'number'),
  field('ReplicationCount', 'Replications', 'Trial', 'number'),
  field('ApplicationCount', 'Applications', 'Trial', 'number'),
  field('VisitCount', 'Visits', 'Trial', 'number'),
  field('PlotSizeL', 'Plot Size L', 'Trial', 'number'),
  field('PlotSizeB', 'Plot Size W', 'Trial', 'number'),
  // Read whichever way the position is stored (utils/gps.js), shown with 5 decimals
  field('PosGPSLatitude', 'Latitude', 'Trial', 'text', { value: (t) => parseGps(t.PosGPSLatitude, t.PosGPSLongitude)?.lat.toFixed(5) }),
  field('PosGPSLongitude', 'Longitude', 'Trial', 'text', { value: (t) => parseGps(t.PosGPSLatitude, t.PosGPSLongitude)?.lng.toFixed(5) }),
  field('EarthSample', 'Earth Sample', 'Trial'),
  samples('NIT_Samples', 'NIT Samples'),
  samples('GT_Samples', 'GT Samples'),
  samples('TGW_Samples', 'TGW Samples'),
  field('Noter1', 'Notes 1', 'Trial'),
  field('Noter2', 'Notes 2', 'Trial'),
  field('Noter3', 'Notes 3', 'Trial'),
  field('Noter4', 'Notes 4', 'Trial'),
  field('Hours', 'Hours', 'Trial', 'number'),
  field('created_at', 'Created', 'Trial', 'date'),

  // Customer, contract & project
  relation('customer_id', 'Customer', 'Customer, contract & project', 'customer.name'),
  field('customer.customerid', 'Customer No', 'Customer, contract & project'),
  field('project.contract.co', CO_LABEL, 'Customer, contract & project'),
  field('project.contract.description', 'Contract Description', 'Customer, contract & project'),
  relation('project_id', 'Protocol No', 'Customer, contract & project', 'project.protocolno'),
  // From TIS (Project.Descr) since 04-11-2026
  field('project.description', 'Project Description', 'Customer, contract & project'),
  // The copy on the trial (the same number, except for some old trials)
  field('PO', `${CO_LABEL} (on trial)`, 'Customer, contract & project', 'number'),
  // The trial's own CO when it differs from its project's (from TIS only)
  field('contract_id', `Own ${CO_LABEL}`, 'Customer, contract & project', 'number', { value: (t) => getPath(t, 'own_contract.co') }),
  field('Contract_Amount', 'Contract Amount', 'Customer, contract & project', 'number'),
  // From TIS since 04-11-2026 ([Price Reduction], [2nd Year Amount])
  field('Price_Reduction', 'Price Reduction', 'Customer, contract & project', 'number'),
  field('Second_Year_Amount', '2nd Year Amount', 'Customer, contract & project', 'number'),
  relation('CustomerContact', 'Customer Contact', 'Customer, contract & project', 'customer_contact.name'),

  // Crop & location
  relation('crop_id', 'Crop', 'Crop & location', 'crop.name'),
  field('crop.description', 'Crop Description', 'Crop & location'),
  field('crop.bbchscale', 'BBCH Scale', 'Crop & location'),
  relation('country_id', 'Country', 'Crop & location', 'country.name'),
  relation('host_id', 'Host', 'Crop & location', 'host.name'),
  field('host.address', 'Host Address', 'Crop & location'),
  field('host.phone', 'Host Phone', 'Crop & location'),
  field('host.email', 'Host Email', 'Crop & location'),
  field('host.country', 'Host Country', 'Crop & location'),
  relation('study_type_id', 'Study Type', 'Crop & location', 'study_type.name'),
  relation('trial_type_id', 'Trial Type', 'Crop & location', 'trial_type.name'),

  // People
  relation('studydirector', 'Study Director', 'People', 'study_director.name'),
  relation('PI', 'Principal Investigator', 'People', 'principal_investigator.name'),
  relation('FT', 'Field Technician', 'People', 'field_technician.name'),
  field('pitxt', 'PI (old text field)', 'People'),
  relation('created_by', 'Created by', 'People', 'creator.name'),

  // Dates & phases (free text in the data, e.g. "Maj 2011")
  field('FieldFaseStart', 'Field Phase Start', 'Dates & phases', 'interval'),
  field('FieldFaseEnd', 'Field Phase End', 'Dates & phases', 'interval'),

  // Activities: the names are the VA-code catalog's (TrialModelObserver), kept identical here so the
  // report and its Excel export need no catalog (tests/e2e checks they match)
  check('ResAr', 'Reserved Area', 'Trial Activities'),
  check('StOut', 'Stacked Out', 'Trial Activities'),
  check('PlotSize', 'Plot Size (W x L)', 'Trial Activities'),
  check('Borders_Sprayed', 'Borders Sprayed', 'Trial Activities'),
  check('Destruction', 'Destruction', 'Trial Activities'),
  check('PegsUp', 'Pegs Up', 'Trial Activities'),
  check('AdditionalWork', 'Optional Work', 'Trial Activities'),
  // Yes / No / To be decided (utils/triState.js)
  field('Harvest', 'Harvest', 'Harvest Activities', 'enum', { options: TRI_STATE }),
  field('NIT', 'NIT', 'Harvest Activities', 'enum', { options: TRI_STATE }),
  field('GerminationTest', 'Germination Test', 'Harvest Activities', 'enum', { options: TRI_STATE }),
  field('TGW', 'TGW', 'Harvest Activities', 'enum', { options: TRI_STATE }),
  check('RecordCleansing', 'Record cleaning', 'Harvest Activities'),
  check('HLLWonly', 'HLW only', 'Harvest Activities'),
  check('EndFieldPhase', 'End Field Phase', 'Report Activities'),
  check('ARMReady', 'ARM Ready for Reporting', 'Report Activities'),
  check('ReportProofRead', 'Draft Report Proofreading', 'Report Activities'),
  check('DraftReport', 'Draft Report', 'Report Activities'),
  check('FinalReport', 'Final Report', 'Report Activities'),
  check('TrialDone', 'Trial Done', 'Report Activities'),
  check('TrialSetupARM', 'Trial Setup ARM', 'Other status'),
  check('WOA', 'WOA', 'Other status'),
  // From TIS since 04-11-2026; their activities (A20, A37) are not shown as tasks
  check('SubcEPA', 'Subc EPA', 'Other status'),
  check('ReadyForBilling', 'Ready for Billing', 'Other status'),
  field('subscribed_GEP_GL', 'Subscribed GEP GL', 'Other status'),
  field('unsubscribed_GEP_GL', 'Unsubscribed GEP GL', 'Other status'),
  // Tasks (diary entries): one report row per entry
  field('diaries.vano', 'VA No', 'Tasks (diary entries)'),
  field('diaries.task', 'Task', 'Tasks (diary entries)'),
  // The customer's Time interval: "<date> · <text>", either alone (Documentation/plans/timeinterval-text.md)
  field('diaries.timeinterval', 'Time Interval', 'Tasks (diary entries)', 'interval', {
    value: (t) => (Array.isArray(t.diaries) ? t.diaries.map((d) => (d ? expectedText(d) || null : null)) : undefined),
  }),
  // Agrolab's Planned (may lie outside the customer's Time interval), as written: a date or e.g. "medio May
  // 2027" (Documentation/plans/planning-dates.md in agromate-api). The key stays, so layouts keep working.
  field('diaries.planned_date', 'Planned Date', 'Tasks (diary entries)', 'interval', {
    value: (t) => (Array.isArray(t.diaries) ? t.diaries.map((d) => d?.planned_interval || d?.planned_date || null) : undefined),
  }),
  field('diaries.bbch', 'BBCH', 'Tasks (diary entries)'),
  field('diaries.persons', 'Persons', 'Tasks (diary entries)'),
  field('diaries.donedate', 'Done Date', 'Tasks (diary entries)', 'date'),
];

/** Every field a layout can offer, in catalog (group) order (not the hidden ones). */
export const REPORT_FIELDS = FIELDS.filter((f) => !f.hidden);

const BY_KEY = new Map(FIELDS.map((f) => [f.key, f]));

// Keys old layouts may contain for a field that is now offered under another key
const ALIASES = {
  'customer.name': 'customer_id',
  'country.name': 'country_id',
  'crop.name': 'crop_id',
  'study_type.name': 'study_type_id',
  'trial_type.name': 'trial_type_id',
  'host.name': 'host_id',
};

/**
 * The definition of a field key. Keys that are no longer offered (internal or non-existing columns
 * in old layouts) still resolve, labelled with their key and read as plain text.
 */
export function reportField(key) {
  return BY_KEY.get(key) ?? BY_KEY.get(ALIASES[key]) ?? field(key, key, 'Other');
}

/** The raw value of a field for a trial (an array for diary fields). */
export function reportFieldValue(def, trial) {
  return def.value ? def.value(trial) : getPath(trial, def.key);
}

/** Whether a value counts as missing (shown as "–" and highlighted in Excel). */
export function isMissing(value) {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

/** The value as text, the way the report shows it. Missing values give ''. */
export function formatReportValue(value, def) {
  if (isMissing(value)) return '';
  switch (def.type) {
    case 'bool':
      return value === true || value === 1 || value === '1' ? 'Yes' : 'No';
    case 'enum':
      return def.options?.[value] ?? String(value);
    case 'date':
      return formatDisplayDate(value) ?? String(value);
    case 'interval':
      return formatDate(value);
    default:
      return String(value);
  }
}
